import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

/**
 * Collapses concurrent identical HTTP requests into a single network call.
 *
 * Keying: `method + urlWithParams + JSON(body)`. While a request is in flight,
 * any other caller hitting the same key piggybacks on the same response via
 * shareReplay — one HTTP roundtrip, every caller gets the response.
 *
 * The cache is cleared as soon as the response arrives (or errors), so this
 * is concurrent-only dedup — sequential calls after the response lands hit
 * the network fresh. That keeps the behaviour intuitive (no stale-data
 * surprises) while eliminating the "many components ask for the same data
 * at the same time" duplication this app sees.
 *
 * Skipped:
 *   - FormData bodies (uploads — not safe to key on, usually one-shot anyway)
 *   - Presigned S3 URLs (each has a unique signature, dedup is pointless)
 */
@Injectable()
export class RequestDedupInterceptor implements HttpInterceptor {
  private inflight = new Map<string, Observable<HttpEvent<any>>>();

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.body instanceof FormData) return next.handle(req);
    if (req.url.includes('X-Amz-Signature')) return next.handle(req);

    const key = `${req.method}|${req.urlWithParams}|${this.bodyKey(req.body)}`;
    const cached = this.inflight.get(key);
    if (cached) return cached;

    const shared = next.handle(req).pipe(
      tap(ev => { if (ev instanceof HttpResponse) this.inflight.delete(key); }),
      finalize(() => this.inflight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.inflight.set(key, shared);
    return shared;
  }

  private bodyKey(b: any): string {
    if (b == null) return '';
    try { return typeof b === 'string' ? b : JSON.stringify(b); } catch { return ''; }
  }
}
